import React, { useEffect, useState } from 'react';
import { Alert, App, Badge, Button, Drawer, Dropdown, Empty, Segmented, Space, Spin, Tooltip, Typography } from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, InfoCircleOutlined, MoreOutlined, WarningOutlined,
} from '@ant-design/icons';

import { isMutable, notificationText } from '@lfoc/domain/notifications/catalogue.js';

import { useAccess } from '../auth/AccessContext.jsx';
import { useNotifications } from './NotificationProvider.jsx';
import { badgeText, groupByDay, hashFor, relativeTime } from './notifications.js';
import { TONE } from '../config/palette.js';

const { Text } = Typography;

/** Each severity's icon and colour (the catalogue's severities; display only). */
const SEVERITY = {
  CRITICAL: { icon: <CloseCircleOutlined />, color: TONE.danger.text },
  WARNING: { icon: <WarningOutlined />, color: TONE.warning.text },
  ACTION: { icon: <ExclamationCircleOutlined />, color: TONE.info.text },
  INFO: { icon: <InfoCircleOutlined />, color: TONE.neutral.text },
};

/**
 * The top bar's bell and its drawer (docs/notifications/implementation-guide.md 8.2).
 *
 *   - The badge is the unread count, kept current by the socket's pushes
 *     (NotificationProvider) - no polling.
 *   - The drawer reads the person's items when it opens: Unread or All,
 *     grouped Today / Yesterday / Earlier in Colombo time, newest first.
 *   - Clicking an item marks it read and opens its screen (its hash route).
 *   - Each item's "..." menu mutes that kind of notification for this person
 *     (not offered for a security notice, which is always sent).
 *   - Items are kept for 7 days (owner, 7 Oct 2026), and the footer says so.
 *
 * @param {{ icon: React.ReactNode }} props  the bell image the top bar already uses
 */
const NotificationBell = ({ icon }) => {
  const { api } = useAccess();
  const { message } = App.useApp();
  const {
    unread, live, items, cursor, loading, error, unreadOnly, loadItems, markRead, markAllRead, drawerOpen, setDrawerOpen,
  } = useNotifications();
  const [now, setNow] = useState(Date.now());

  // Read the page when the drawer opens; its relative times tick while it is open.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    loadItems({ unreadOnly });
    setNow(Date.now());
    const tick = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(tick);
    // Opening is the trigger; loadItems is stable enough for that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen]);

  const open = async (item) => {
    if (!item.readAt) await markRead([item.id]);
    const hash = hashFor(item.link);
    if (hash) {
      window.location.hash = hash;
      setDrawerOpen(false);
    }
  };

  const mute = async (code) => {
    try {
      await api.notifications.setMuted(code, true);
      message.success('Muted: ' + notificationText(code).label + '. You can turn it back on in your profile.');
    } catch (e) {
      message.error(e.message);
    }
  };

  const groups = groupByDay(items, now);

  return (
    <>
      <Tooltip title={live ? 'Notifications (live)' : 'Notifications'}>
        <Badge count={unread ? badgeText(unread) : 0} size="small" offset={[-4, 4]}>
          <Button className="console-topbar-icon" type="text" icon={icon} aria-label={'Notifications' + (unread ? ', ' + unread + ' unread' : '')}
            onClick={() => setDrawerOpen(true)} />
        </Badge>
      </Tooltip>
      <Drawer
        title="Notifications"
        placement="right"
        width={420}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        rootClassName="lfoc-styles-wrapper notification-drawer"
        extra={<Button size="small" icon={<CheckCircleOutlined />} disabled={!unread} onClick={markAllRead}>Mark all read</Button>}
        footer={<Text type="secondary" style={{ fontSize: 12 }}>Notifications are kept for 7 days. Change what you receive in your profile.</Text>}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Segmented
            block
            value={unreadOnly ? 'unread' : 'all'}
            onChange={(v) => loadItems({ unreadOnly: v === 'unread' })}
            options={[{ label: 'Unread', value: 'unread' }, { label: 'All', value: 'all' }]}
          />
          {error && <Alert type="error" showIcon message={error} />}
          <Spin spinning={loading}>
            {!groups.length && !loading && (
              <Empty description={unreadOnly ? 'Nothing unread' : 'No notifications in the last 7 days'} />
            )}
            {groups.map((group) => (
              <div key={group.label} className="notification-group">
                <div className="section-label">{group.label}</div>
                {group.items.map((item) => {
                  const look = SEVERITY[item.severity] ?? SEVERITY.INFO;
                  return (
                    <div
                      key={item.id}
                      className={'notification-item' + (item.readAt ? ' is-read' : '')}
                      role="button"
                      tabIndex={0}
                      onClick={() => open(item)}
                      onKeyDown={(e) => { if (e.key === 'Enter') open(item); }}
                      style={{ display: 'flex', gap: 10, padding: '8px 4px', cursor: 'pointer', opacity: item.readAt ? 0.65 : 1 }}
                    >
                      <span style={{ color: look.color, fontSize: 18, lineHeight: '22px' }}>{look.icon}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text strong={!item.readAt}>{item.title}</Text>
                        <div><Text type="secondary" style={{ fontSize: 12 }}>{item.body}</Text></div>
                        <Text type="secondary" style={{ fontSize: 11 }}>{relativeTime(item.createdAt, now)}</Text>
                      </div>
                      {isMutable(item.code) && (
                        <Dropdown
                          trigger={['click']}
                          menu={{ items: [{ key: 'mute', label: 'Mute "' + notificationText(item.code).label + '"' }], onClick: () => mute(item.code) }}
                        >
                          <Button type="text" size="small" icon={<MoreOutlined />} aria-label="More" onClick={(e) => e.stopPropagation()} />
                        </Dropdown>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
            {cursor && (
              <Button block type="link" onClick={() => loadItems({ more: true })}>Show older</Button>
            )}
          </Spin>
        </Space>
      </Drawer>
    </>
  );
};

export default NotificationBell;
